import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { adminVendorsList, adminVendorsRetrieve } from "@motolynk/api-client";
import type { VendorPickerProps } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { Combobox } from "@motolynk/ui";

import { vendorKeys } from "./shared";

/** The `vendor.picker` slot: search vendors by name, email or phone and choose one (any status). */
export function VendorPicker({ id, value, onChange, invalid }: VendorPickerProps) {
  const { t } = useTranslation("vendors");
  const [text, setText] = useState("");
  const query = { page_size: 20, ordering: "name" as const, ...(text ? { search: text } : {}) };
  const results = useQuery({
    queryKey: vendorKeys.list(query),
    queryFn: async () => (await adminVendorsList(query)).data,
  });
  const chosen = useQuery({
    queryKey: vendorKeys.detail(value ?? ""),
    queryFn: async () => (await adminVendorsRetrieve(value ?? "")).data,
    enabled: Boolean(value),
  });
  return (
    <Combobox
      id={id}
      aria-invalid={invalid}
      value={value}
      selectedLabel={chosen.data?.store_name}
      options={(results.data?.results ?? []).map((vendor) => ({
        value: vendor.uid,
        label: vendor.store_name,
        description: `${t(`status.${vendor.status}`)} · ${vendor.legal_name}`,
      }))}
      onSearch={setText}
      onChange={(uid) => onChange(uid)}
      loading={results.isFetching && !results.data}
      loadingLabel={t("picker.loading")}
      placeholder={t("picker.placeholder")}
      emptyLabel={t("picker.empty")}
      clearLabel={t("picker.clear")}
    />
  );
}
